import { combineReducers } from "redux";
import { withReduxStateSync } from "redux-state-sync";

import { cptModifiersReducer } from "../features/cptModifiers/reducer";

import { adviceReducer } from "./advice";
import { adviceErrorsReducer } from "./adviceErrors";
import { adviceSearchQueryReducer } from "./adviceSearchQuery";
import { adviceSearchResultReducer } from "./adviceSearchResult";
import announcements from "./announcements";
import { b2cReducer } from "./b2c";
import { choiceListsReducer } from "./choiceLists";
import { choiceListsSettingsReducer } from "./choiceListsSettings";
import { codeFormatsReducer } from "./codeFormats";
import { codeSetsExportReducer } from "./codeSetsExport";
import { codesInfoReducer } from "./codesInfo";
import { codesetsReducer } from "./codesets";
import { codingAdvicesReducer } from "./codingAdvices";
import { customizationReducer } from "./customization";
import { drgAnalyzeReducer } from "./drgAnalyze";
import { elixhauserAnalyzeReducer } from "./elixhauserAnalyze";
import { encoderUserListReducer } from "./encoderUserList";
import { encounterReducer } from "./encounter";
import { encounterImportExportFormatsReducer } from "./encounterImportExportFormats";
import { encounterTemplatesReducer } from "./encounterTemplates";
import { expandedCodeReducer } from "./expandedCode";
import { exportEncountersReducer } from "./exportEncounters";
import { facilitiesReducer } from "./facilities";
import { facilitySettingsReducer } from "./facilitySettings";
import { financialClassesReducer } from "./financialClasses";
import { getArticleReducer } from "./getArticle";
import { getPolicyArticleReducer } from "./getPolicyArticle";
import { groupingInfoReducer } from "./groupingInfo";
import { importEncountersReducer } from "./importEncounters";
import { importProvidersReducer } from "./importProviders";
import { licenseReducer } from "./license";
import { lookupReducer } from "./lookups";
import { manageEncoderUserReducer } from "./manageUsers";
import { netActivityReducer } from "./netActivity";
import { pdxAnalyzeReducer } from "./pdxAnalyze";
import { practicesReducer } from "./practices";
import { preferencesReducer } from "./preferences";
import { recoveredEncountersReducer } from "./recoveredEncounters";
import { searchEncountersReducer } from "./searchEncounters";
import { snapshotReducer } from "./snapshot";
import { synonymsReducer } from "./synonyms";
import { systemPreferencesReducer } from "./systemPreferences";
import { uiReducer } from "./ui";
import { userReducer } from "./user";
import { userActivityReducer } from "./userActivity";
import { userFeaturesReducer } from "./userFeatures";

import type { CPTModifiersState } from "../features/cptModifiers/models";
import type {
  AdviceErrors,
  AdviceSearchQueryEntity,
  AdviceSearchResultState,
  AdviceState,
  ArticleState,
  CodeRangeExpansionState,
  EncoderUserListState,
  LookupState,
  ManageEncoderUserState,
  PolicyArticleState,
  UI,
  UserFeaturesState,
  UserState,
} from "../models";
import type { AnnouncementsState } from "../models/announcements";
import type { B2CState } from "../models/b2c";
import type { CodeFormatsState } from "../models/codeFormats";
import type { CodeSetsExportState } from "../models/codeSetsExport";
import type { CodesInfo } from "../models/codesInfo";
import type { CodeSetsState } from "../models/codesets";
import type { CodingAdvicesState } from "../models/codingAdvices";
import type { CustomizationState } from "../models/customization";
import type { ElixhauserAnalyzeState } from "../models/elixhauser";
import type {
  EncounterExportState,
  EncounterImportExportFormatsState,
  EncounterImportState,
  EncounterSnapshotState,
  EncounterState,
  RecoveredEncountersState,
} from "../models/encounterEntity";
import type { ProvidersImportState } from "../models/encounterServiceEntity";
import type { EncounterTemplates } from "../models/encounterTemplate";
import type { FacilitySettingsState } from "../models/facilitySettings";
import type { DrgAnalyzeState, GroupingInfoState, PDxAnalyzeState } from "../models/groupingEntity";
import type { LicenseState } from "../models/license";
import type { NetActivityState } from "../models/netActivity";
import type { ChoiceListsState } from "../models/patientEncounter";
import type { SearchEncounters } from "../models/searchEncounterFilter";
import type { SynonymsState } from "../models/synonyms";
import type { UserActivityState } from "../models/userActivity";
import type { ChoiceListsSettingsState } from "../scenes/ChoiceListsSettings/models/choiceListsSettingsState";
import type { FacilitiesState } from "../scenes/Facility/models/facilitiesState";
import type { SettingsState } from "../scenes/FacilityPreferences/models/settingsState";
import type { FinancialClassState } from "../scenes/FinancialClass/models/financialClassState";
import type { PracticesState } from "../scenes/Practice/models/practicesState";
import type { SystemPreferencesState } from "../scenes/SystemPreferences/models/systemPreferencesState";

export interface State {
  advice: AdviceState;
  adviceErrors: AdviceErrors;
  adviceSearchQuery: AdviceSearchQueryEntity;
  adviceSearchResult: AdviceSearchResultState;
  announcements: AnnouncementsState;
  article: ArticleState;
  b2c: B2CState;
  choiceLists: ChoiceListsState;
  choiceListsSettings: ChoiceListsSettingsState;
  codeFormats: CodeFormatsState;
  drgAnalyze: DrgAnalyzeState;
  encoderUserList: EncoderUserListState;
  encounter: EncounterState;
  encounterSnapshot: EncounterSnapshotState;
  encounterTemplates: EncounterTemplates;
  expandedCodeRange: CodeRangeExpansionState;
  facilities: FacilitiesState;
  facilitySettings: FacilitySettingsState;
  practices: PracticesState;
  financialClasses: FinancialClassState;
  groupingInfo: GroupingInfoState;
  license: LicenseState;
  lookups: LookupState;
  manageEncoderUser: ManageEncoderUserState;
  pdxAnalyze: PDxAnalyzeState;
  elixhauserAnalyze: ElixhauserAnalyzeState;
  policyArticle: PolicyArticleState;
  recoveredEncounters: RecoveredEncountersState;
  searchEncounters: SearchEncounters;
  exportEncounters: EncounterExportState;
  importEncounters: EncounterImportState;
  providersImport: ProvidersImportState;
  encounterImportExportFormats: EncounterImportExportFormatsState;
  settings: SettingsState;
  ui: UI;
  user: UserState;
  userFeatures: UserFeaturesState;
  userActivity: UserActivityState;
  netActivity: NetActivityState;
  systemPreferences: SystemPreferencesState;
  customization: CustomizationState;
  codesets: CodeSetsState;
  codeSetsExport: CodeSetsExportState;
  codingAdvices: CodingAdvicesState;
  synonyms: SynonymsState;
  codesInfo: CodesInfo;
  cptModifiers: CPTModifiersState;
}

export const createRootReducer = () =>
  withReduxStateSync(
    combineReducers<State>({
      advice: adviceReducer,
      adviceErrors: adviceErrorsReducer,
      adviceSearchQuery: adviceSearchQueryReducer,
      adviceSearchResult: adviceSearchResultReducer,
      announcements,
      article: getArticleReducer,
      b2c: b2cReducer,
      choiceLists: choiceListsReducer,
      choiceListsSettings: choiceListsSettingsReducer,
      codeFormats: codeFormatsReducer,
      drgAnalyze: drgAnalyzeReducer,
      encoderUserList: encoderUserListReducer,
      encounter: encounterReducer,
      encounterSnapshot: snapshotReducer,
      encounterTemplates: encounterTemplatesReducer,
      expandedCodeRange: expandedCodeReducer,
      facilities: facilitiesReducer,
      facilitySettings: facilitySettingsReducer,
      practices: practicesReducer,
      financialClasses: financialClassesReducer,
      groupingInfo: groupingInfoReducer,
      license: licenseReducer,
      lookups: lookupReducer,
      manageEncoderUser: manageEncoderUserReducer,
      pdxAnalyze: pdxAnalyzeReducer,
      elixhauserAnalyze: elixhauserAnalyzeReducer,
      policyArticle: getPolicyArticleReducer,
      recoveredEncounters: recoveredEncountersReducer,
      searchEncounters: searchEncountersReducer,
      exportEncounters: exportEncountersReducer,
      importEncounters: importEncountersReducer,
      providersImport: importProvidersReducer,
      encounterImportExportFormats: encounterImportExportFormatsReducer,
      settings: preferencesReducer,
      ui: uiReducer,
      user: userReducer,
      userFeatures: userFeaturesReducer,
      userActivity: userActivityReducer,
      netActivity: netActivityReducer,
      systemPreferences: systemPreferencesReducer,
      customization: customizationReducer,
      codesets: codesetsReducer,
      codeSetsExport: codeSetsExportReducer,
      codingAdvices: codingAdvicesReducer,
      synonyms: synonymsReducer,
      codesInfo: codesInfoReducer,
      cptModifiers: cptModifiersReducer,
    }),
  );
